import { z } from 'zod'
import { oc } from '@orpc/contract'
import { campaignSchema } from './crm-campaign.contract'

/**
 * Population cohorts for CRM campaigns (PD-1993, docs/CRM.md): a draft's recipients can be
 * every patient open now in one or more of Population Health's cohorts — a family group, a
 * condition (family) or a measure. Read as the caller, as Population reads them, and only the
 * CRM project's patients. Same access as the rest of the CRM.
 */

const countsShape = {
  /** Patients with an open finding in the cohort now, each once. */
  patients: z.number().int().min(0),
  /** Of them, those whose highest grade across all their open findings is high. */
  highRisk: z.number().int().min(0),
}

export const populationCohortRefSchema = z.object({
  type: z.enum(['group', 'family', 'measure']),
  slug: z.string().min(1),
})

/** One cohort, or several: a patient in any of them counts once. */
const cohortsSchema = z.array(populationCohortRefSchema).min(1).max(100)

/** Every cohort the CRM can offer, group → condition → measure, with today's counts. */
export const crmPopulationCohorts = oc
  .route({ method: 'GET', path: '/v2/crm/population-cohorts' })
  .output(
    z.object({
      groups: z.array(
        z.object({
          slug: z.string(),
          label: z.string(),
          ...countsShape,
          families: z.array(
            z.object({
              slug: z.string(),
              label: z.string(),
              ...countsShape,
              measures: z.array(z.object({ slug: z.string(), label: z.string(), ...countsShape })),
            }),
          ),
        }),
      ),
    }),
  )

/** How many patients a selection holds, and the most one add takes. */
export const crmPopulationCohortCount = oc
  .route({ method: 'POST', path: '/v2/crm/population-cohorts/count' })
  .input(z.object({ cohorts: cohortsSchema }))
  .output(z.object({ patients: z.number().int().min(0), max: z.number().int() }))

/**
 * Adds a selection's patients to a draft: a lead is created for each patient with a textable
 * phone who isn't one yet (as Import existing patients does); patients without one are left
 * out. Refused above the add cap rather than cut short.
 */
export const crmCohortAddPopulation = oc
  .route({ method: 'POST', path: '/v2/crm/campaigns/cohort/add-population' })
  .input(z.object({ campaignId: z.number().int(), cohorts: cohortsSchema }))
  .output(
    z.object({
      added: z.number().int().min(0),
      /** Their lead was already a recipient. */
      alreadyIn: z.number().int().min(0),
      /** Patients with no textable phone (or no chart the caller can read). */
      leftOut: z.number().int().min(0),
      /** Leads created for patients who weren't in the CRM. */
      created: z.number().int().min(0),
      campaign: campaignSchema,
    }),
  )
